import { useMemo } from "react";
import { useLocation } from "wouter";
import { Mail, MessageCircle, ShieldCheck, UserCircle2 } from "lucide-react";
import SalviaLayout from "@/components/SalviaLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";

const WHATSAPP_SUPPORT_URL = "https://wa.me/554891546444?text=Ol%C3%A1%2C%20preciso%20de%20ajuste%20no%20Portal%20da%20Jornada%20do%20Cliente.";

function getFriendlyProfileLabel(rawLabel?: string | null) {
  const value = (rawLabel || "").trim();
  const normalized = value.toLowerCase();

  if (!value) return "Seu acesso principal";
  if (normalized.includes("rh") && normalized.includes("cliente")) return "Responsável da empresa";
  if (normalized.includes("client") || normalized.includes("cliente")) return "Acesso da empresa";
  if (normalized.includes("channel") || normalized.includes("canal")) return "Acesso do canal";
  if (normalized.includes("admin")) return "Acesso de gestão";
  if (normalized.includes("consult")) return "Acesso de consultoria";

  return value;
}

export default function PortalMinhaConta() {
  usePageTitle("Minha Conta");

  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const currentProfileLabel = getFriendlyProfileLabel(
    String((user as any)?.platformNucleus?.currentProfile?.label || ""),
  );

  const currentCompanyLabel = String(
    (user as any)?.platformNucleus?.currentScope?.company?.displayName
      || (user as any)?.platformNucleus?.currentScope?.channel?.displayName
      || "Sua conta principal",
  );

  const userName = String((user as any)?.name || "Não informado");
  const userEmail = String((user as any)?.email || "Não informado");

  const summaryCards = useMemo(
    () => [
      { label: "Nome", value: userName, icon: <UserCircle2 className="h-4 w-4" /> },
      { label: "E-mail", value: userEmail, icon: <Mail className="h-4 w-4" /> },
      { label: "Seu acesso", value: currentProfileLabel, icon: <ShieldCheck className="h-4 w-4" /> },
      { label: "Empresa atual", value: currentCompanyLabel, icon: <UserCircle2 className="h-4 w-4" /> },
    ],
    [currentCompanyLabel, currentProfileLabel, userEmail, userName],
  );

  return (
    <SalviaLayout title="Minha Conta">
      <div className="space-y-6">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(190,242,100,0.18),_transparent_30%),linear-gradient(135deg,_#041833_0%,_#0b2746_48%,_#102f57_100%)] p-6 text-white shadow-[0_28px_90px_-40px_rgba(2,6,23,0.85)] lg:p-8">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-200/90">
              <button type="button" onClick={() => setLocation("/portal")} className="transition-colors hover:text-white">
                Portal da Jornada do Cliente
              </button>
              <span>/</span>
              <span className="font-medium text-white">Minha Conta</span>
            </div>
            <Badge className="w-fit rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-lime-100 hover:bg-lime-300/10">
              Seus dados pessoais
            </Badge>
            <div className="space-y-3">
              <h1 className="max-w-4xl text-3xl font-semibold tracking-tight text-white lg:text-4xl">
                Aqui ficam apenas as informações do seu acesso.
              </h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-200/90 lg:text-base">
                Consulte rapidamente seus dados, confirme o perfil ativo e veja qual empresa está em uso neste momento.
              </p>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => (
            <Card key={card.label} className="rounded-[1.6rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                <span className="text-slate-700">{card.icon}</span>
                {card.label}
              </div>
              <p className="mt-3 text-sm font-medium leading-6 text-slate-900">{card.value}</p>
            </Card>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Seu acesso em um relance</h2>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  Esta área serve apenas para conferência rápida do seu nome, e-mail, perfil ativo e empresa vinculada.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => setLocation("/portal")}>Voltar ao início</Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Atualizar dados da tela
              </Button>
            </div>
          </Card>

          <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#eef5fb] text-[#0b3b6f]">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Precisa de ajuste?</h2>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  Se houver correção de cadastro, acesso ou vínculo da empresa, fale diretamente com o suporte responsável pela sua conta.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => window.open(WHATSAPP_SUPPORT_URL, "_blank", "noopener,noreferrer")}>
                Falar com suporte no WhatsApp
              </Button>
              <p className="self-center text-xs text-slate-500">Atendimento: +55 48 9154-6444</p>
            </div>
          </Card>
        </section>
      </div>
    </SalviaLayout>
  );
}
